import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { auth } from "../firebase";

const API_URL = `${process.env.REACT_APP_SERVER_URL}/api`;

interface RequestConfig {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  id?: string;
  data?: any;
  params?: string;
  basePath?: string;
}

export async function request<T>({
  method = "GET",
  id,
  data,
  params = "",
  basePath,
}: RequestConfig = {}): Promise<any> {
  try {
    // Get fresh token (Firebase automatically handles refreshing if expired)
    const TOKEN = auth.currentUser ? await auth.currentUser.getIdToken() : null;
    
    let url = API_URL + basePath;

    if (id) {
      url += `?id=${id}`;
      if (params) url += `&${params.replace(/^\?/, "")}`;
    } else if (params) {
      url += params.startsWith("?") ? params : `?${params}`;
    }

    const config: AxiosRequestConfig = {
      method,
      url,
      headers: {
        "Content-Type": "application/json",
        ...(TOKEN ? { Authorization: `Bearer ${TOKEN}` } : {}),
      },
      ...(data ? { data } : {}),
    };

    const response: AxiosResponse = await axios<T>(config);

    if (response.status === 204) return null;
    return response.data;
  } catch (err: any) {
    if (err.response?.status !== 401) {
       alert(err.message);
    }
    throw err;
  }
}

export async function exportCsvData(basePath: string, params: string = ""): Promise<void> {
  try {
    const TOKEN = auth.currentUser ? await auth.currentUser.getIdToken() : null;
    let url = API_URL + basePath;
    
    // Add params and export=true flag
    if (params) {
      url += params.startsWith("?") ? params : `?${params}`;
      url += "&export=true";
    } else {
      url += "?export=true";
    }

    const response = await axios({
      method: "GET",
      url,
      headers: {
        ...(TOKEN ? { Authorization: `Bearer ${TOKEN}` } : {}),
      },
      responseType: "blob",
    });

    const contentDisposition = response.headers['content-disposition'];
    let filename = 'export.csv';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch && filenameMatch.length === 2) {
        filename = filenameMatch[1];
      }
    }

    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (err: any) {
    console.error("Export failed:", err);
    alert("שגיאה בייצוא הנתונים");
  }
}

export async function importData(basePath: string, data: any[]): Promise<any> {
  try {
    const TOKEN = auth.currentUser ? await auth.currentUser.getIdToken() : null;
    const url = `${API_URL}${basePath}?import=true`;

    const response = await axios({
      method: "POST",
      url,
      headers: {
        "Content-Type": "application/json",
        ...(TOKEN ? { Authorization: `Bearer ${TOKEN}` } : {}),
      },
      data,
    });

    return response.data;
  } catch (err: any) {
    if (err.response?.data?.errors) {
      throw err.response.data; // This will contain the array of errors: { row, column, message }
    }
    console.error("Import failed:", err);
    throw new Error(err.response?.data?.error || "שגיאה בייבוא הנתונים");
  }
}

export async function downloadTemplate(basePath: string): Promise<void> {
  try {
    const TOKEN = auth.currentUser ? await auth.currentUser.getIdToken() : null;
    const url = `${API_URL}${basePath}?template=true`;

    const response = await axios({
      method: "GET",
      url,
      headers: {
        ...(TOKEN ? { Authorization: `Bearer ${TOKEN}` } : {}),
      },
      responseType: "blob",
    });

    const contentDisposition = response.headers['content-disposition'];
    let filename = 'template.csv';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch && filenameMatch.length === 2) {
        filename = filenameMatch[1];
      }
    }

    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (err: any) {
    console.error("Template download failed:", err);
    alert("שגיאה בהורדת התבנית");
  }
}


